import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Command } from 'cmdk'
import { uniq } from 'lodash'
import { useRouter } from 'next/router'

import { Modal } from '../shared-components/BUI/Modal'
import { Text } from '../shared-components/BUI/Text'
import { Flexbox } from '../shared-components/Flexbox'
import {
  ProjectIcon,
  ProjectIconWrapper,
  getProjectColour,
} from '../shared-components/ProjectIcon'

import { CommandKBarStyles } from './CommandKBar/CommandKBarStyles'
import {
  CMD_K_ITEMS,
  CommandAction,
  CommandGroup,
  CommandType,
} from './CommandKBar/config'
import { DynamicChecklistActions } from './CommandKBar/DynamicChecklistActions'
import { DynamicProjectActions } from './CommandKBar/DynamicProjectActions'
import { DynamicRegisterItemActions } from './CommandKBar/DynamicRegisterItemActions'
import { DynamicSiteInductionActions } from './CommandKBar/DynamicSiteInductionActions'
import { DynamicSubcontractorActions } from './CommandKBar/DynamicSubcontractorActions'
import { DynamicTicketActions } from './CommandKBar/DynamicTicketActions'
import { DynamicWorkerActions } from './CommandKBar/DynamicWorkerActions'
import { RenderItem } from './CommandKBar/RenderItem'

import { useCommandKBar } from '@/context/commandKBarContext'
import { useLocale } from '@/context/localeContext'
import { useProject } from '@/context/projectContext'
import { useSwitchBuilderModal } from '@/context/switchBuilderContext'
import { useDebounce } from '@/hooks/useDebounce'
import { Role } from '@/modules/adminUsers/roles'
import { truncateWithTooltip, useRoles } from '@/utils/index'

const DYNAMIC_ACTIONS = [
  DynamicProjectActions,
  DynamicWorkerActions,
  DynamicSubcontractorActions,
  DynamicChecklistActions,
  DynamicSiteInductionActions,
  DynamicTicketActions,
  DynamicRegisterItemActions,
]

const tryFindItem = (
  items: typeof CMD_K_ITEMS,
  id: string
): CommandAction | CommandGroup | null => {
  for (const item of items) {
    if (item.id === id) return item

    if ('groupActions' in item) {
      const subItem = tryFindItem(item.groupActions, id)
      if (subItem) return subItem
    }

    if ('subActions' in item) {
      const subItem = tryFindItem(item.subActions, id)
      if (subItem) return subItem
    }
  }

  return null
}

const cloneItems = (items: typeof CMD_K_ITEMS) => {
  return items.map((item) => {
    if ('groupActions' in item) {
      return { ...item, groupActions: cloneItems(item.groupActions) }
    }
    if ('subActions' in item) {
      return { ...item, subActions: cloneItems(item.subActions) }
    }
    return { ...item }
  })
}

export const CommandKBar = () => {
  const { t } = useLocale()
  const router = useRouter()
  const inputElement = useRef<HTMLInputElement>(null)

  const { isProjectSelected, project } = useProject()
  const { open, openCommandKBar, closeCommandKBar } = useCommandKBar()
  const { openSwitchBuilderModal } = useSwitchBuilderModal()
  const { hasRole } = useRoles()

  const [pages, setPages] = useState<string[]>([])
  const [itemConfig, setItemConfig] = useState(CMD_K_ITEMS)
  const [searchText, setSearchText] = useState<string>('')

  const debouncedSearchText = useDebounce(searchText)

  const closeModal = useCallback(() => {
    setPages([])
    setSearchText('')
    setItemConfig(CMD_K_ITEMS)
    closeCommandKBar()
  }, [closeCommandKBar])

  const addSubItemsToParent = useCallback(
    (parentId: string, subItems: Array<CommandType>) => {
      setItemConfig((items) => {
        const newItems = cloneItems(items)
        const parent = tryFindItem(newItems, parentId)

        if (parent) {
          if ('groupTitle' in parent) {
            parent.groupActions = [
              ...parent.groupActions,
              ...(subItems as CommandAction[]),
            ]
          } else if ('name' in parent) {
            parent.subActions = [...(parent.subActions ?? []), ...subItems]
          }
        }

        return newItems
      })
    },
    []
  )

  const replaceParentSubItems = useCallback(
    (parentId: string, subItems: Array<CommandType>) => {
      setItemConfig((items) => {
        const newItems = cloneItems(items)
        const parent = tryFindItem(newItems, parentId)

        if (parent) {
          if ('groupTitle' in parent) {
            parent.groupActions = subItems as CommandAction[]
          } else if ('name' in parent) {
            parent.subActions = subItems
          }
        }

        return newItems
      })
    },
    []
  )

  useEffect(() => {
    const onGlobalKeydown = (e) => {
      // on cmd+k key down
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        openCommandKBar()
      }

      // on cmd+b key down
      if (hasRole([Role.SuperAdmin])) {
        if (e.key === 'b' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault()
          openSwitchBuilderModal()
        }
      }
    }

    document.addEventListener('keydown', onGlobalKeydown)
    router.events.on('routeChangeStart', closeModal)

    return () => {
      document.removeEventListener('keydown', onGlobalKeydown)
      router.events.off('routeChangeStart', closeModal)
    }
  }, [
    router.events,
    closeModal,
    openCommandKBar,
    openSwitchBuilderModal,
    hasRole,
  ])

  const resetSearchText = () => {
    setSearchText('')
    inputElement?.current?.focus()
  }

  const addPage = (page: string) => {
    setPages((pages) => uniq([...(pages ?? []), page]))
    resetSearchText()
  }

  const setPage = (index: number) => {
    setPages((pages) => pages.slice(0, index))
    resetSearchText()
  }

  const onInputKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Backspace' && !searchText) {
      e.preventDefault()
      setPages((pages) => pages.slice(0, -1))
    }
  }

  const [items, pageTitles, filterResults] = useMemo<
    [typeof CMD_K_ITEMS, string[], boolean]
  >(() => {
    if (!pages?.length) return [itemConfig, [], true]

    let index = 0
    let currentItems: typeof CMD_K_ITEMS = itemConfig
    let currentItem: (typeof CMD_K_ITEMS)[number] = null
    let filterResults = true
    const pageTitles = []

    do {
      const page = pages[index]
      currentItem = tryFindItem(currentItems, page)

      if (!currentItem) break

      if ('subActions' in currentItem) {
        currentItems = currentItem.subActions
        filterResults = currentItem.filterResults ?? true
        pageTitles.push(currentItem?.name)
      } else {
        break
      }

      index += 1
    } while (index <= pages.length)

    return [currentItems, pageTitles, filterResults]
  }, [itemConfig, pages])

  const placeholder = useMemo(() => {
    if (!filterResults) {
      return t(pageTitles[pageTitles.length - 1]) + '...'
    }
    return 'What do you need?'
  }, [pageTitles, filterResults, t])

  return (
    <>
      <CommandKBarStyles />
      <Modal
        open={open}
        onCancel={closeModal}
        className="cmdk-vercel"
        noPadding
        maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
        closable={false}
        destroyOnClose
      >
        {DYNAMIC_ACTIONS.map((DynamicComponent, index) => (
          <DynamicComponent
            key={index}
            activePage={pages[pages.length - 1]}
            debouncedSearchText={debouncedSearchText}
            addSubItemsToParent={addSubItemsToParent}
            replaceParentSubItems={replaceParentSubItems}
          />
        ))}

        <Command
          label="BuildPass Command K"
          shouldFilter={filterResults}
          onKeyDown={onInputKeydown}
        >
          <Flexbox
            alignItems="center"
            justifyContent="space-between"
            gap="0.5rem"
          >
            <div>
              <div cmdk-vercel-badge="" onClick={() => setPage(0)}>
                Home
              </div>
              {pageTitles.map((title, index) => (
                <div
                  key={title + index}
                  onClick={() => setPage(index + 1)}
                  cmdk-vercel-badge=""
                >
                  {t(title)}
                </div>
              ))}
            </div>
            {isProjectSelected && (
              <ProjectIconWrapper bold={false}>
                <ProjectIcon colour={getProjectColour(project)} size={16} />
                <Text variant="caption01">
                  {truncateWithTooltip(project.name, 20)}
                </Text>
              </ProjectIconWrapper>
            )}
          </Flexbox>
          <Command.Input
            autoFocus
            value={searchText}
            onValueChange={setSearchText}
            placeholder={placeholder}
            ref={inputElement}
          />
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>

            {items.map((item) => (
              <RenderItem
                key={item.id}
                item={item}
                addPage={addPage}
                searchText={searchText}
                closeModal={closeModal}
              />
            ))}
          </Command.List>
        </Command>
      </Modal>
    </>
  )
}
